import { Box, font, themedColor, usePageScroll } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis';
import { lerp } from '@lemonade-hq/ts-helpers';
import React from 'react';
import { Helmet } from 'react-helmet';
import styled, { css } from 'styled-components';
import { useElementLerp } from '../../hooks/useElementLerp';
import useValueAtRest from '../Giveback2021/useValueAtRest';
import {
  CLASS_NAME,
  PHRASES_INDICES,
  SNAKE_LENGTH,
  TEXT_ARRAY,
  generateCivilRightsStyle,
  generateEnvironmentStyle,
  generateLgbtqStyle,
  generateRestAnimationStyle,
  generateSnakeStyle,
} from './Giveback2022Helpers';

const StyledContainer = styled(Box)`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  color: ${themedColor('veryLightGrey')};
  will-change: transform;

  ${MEDIA_QUERIES.mobile`
        letter-spacing: -2px;
        ${font('main', {
          fontSize: '48px',
          lineHeight: '56px',
          fontWeight: 800,
        })};
    `}

  ${MEDIA_QUERIES.tablet`
        letter-spacing: -2px;
        ${font('main', {
          fontSize: '70px',
          lineHeight: '80px',
          fontWeight: 800,
        })};
    `}

    ${MEDIA_QUERIES.desktop`
        letter-spacing: -4px;
        ${font('main', {
          fontSize: '125px',
          lineHeight: '143px',
          fontWeight: 800,
        })};
    `}

    > :last-child {
    flex: 1;
  }
`;

const StyledChar = styled.div<{ bold: boolean }>`
  display: inline-block;
  transition: color 200ms linear;

  ${MEDIA_QUERIES.mobile`
        word-spacing: 0.55rem;
    `}

  ${MEDIA_QUERIES.tablet`
        word-spacing: 0.6rem;
    `}

    ${MEDIA_QUERIES.desktop`
        word-spacing: 1rem;
    `}

    ${({ bold }) =>
    bold &&
    css`
      color: ${themedColor('darkGray')};
    `}
`;

const Chars = React.memo(() => {
  return (
    <>
      {TEXT_ARRAY.map((char, charIndex) => {
        return (
          <StyledChar
            // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
            key={charIndex}
            bold={PHRASES_INDICES.includes(charIndex)}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
            dangerouslySetInnerHTML={{
              __html: char === ' ' ? '&nbsp' : char,
            }}
          />
        );
      })}
    </>
  );
});

Chars.displayName = 'Chars';

const Giveback2022Content: React.FC = () => {
  const { absoluteScroll: t } = usePageScroll();
  const [ref, newT] = useElementLerp(t, {
    startPoint: 0.6,
  });

  const snakeIndex = Math.floor(lerp(-SNAKE_LENGTH, TEXT_ARRAY.length, newT));
  const resting = useValueAtRest({ changingValue: newT, duration: 2000 });

  let style = `@keyframes pulse {
        0% { transform:scaleY(1)}
        10% { transform:scaleY(1.05)}
        100% { transform:scaleY(1)}
    }
    `;

  style += generateSnakeStyle(snakeIndex);
  style += generateEnvironmentStyle(snakeIndex);
  style += generateCivilRightsStyle(snakeIndex);
  style += generateLgbtqStyle(snakeIndex);

  if (resting) {
    style += generateRestAnimationStyle(snakeIndex);
  }

  return (
    <>
      <Helmet>
        <style type="text/css">{style}</style>
      </Helmet>
      <StyledContainer className={CLASS_NAME} ref={ref}>
        <Chars />
      </StyledContainer>
    </>
  );
};

export default Giveback2022Content;
